import React from 'react';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonProgressBar,
} from '@ionic/react';
import { closeOutline, arrowBackOutline } from 'ionicons/icons';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { pxToRem } from '@/modules/config/constants';

interface ShareWizardHeaderProps {
  topicTitle: string;
  progress: number;
  /** When set, shown as the visible centered toolbar title for this step. */
  title?: string;
  /** When set, appended after the topic title and never truncated. */
  progressLabel?: string;
  onBack?: () => void;
}

const ShareWizardHeader: React.FC<ShareWizardHeaderProps> = ({
  topicTitle,
  progress,
  title,
  progressLabel,
  onBack,
}) => {
  const {
    sendQueue,
    inProgressLegId,
    confirmedContactIds,
    nav,
    setShowCloseWarning,
  } = useShareWizardContext();

  // The "X" leaves the whole flow from any destination (issue #295). Only warn first
  // when sharing is actually underway — since bailing then risks
  // duplicates/unsent recipients. Before sharing starts there's nothing to lose, so just exit.
  const isSending =
    sendQueue.length > 0 &&
    (inProgressLegId !== null || confirmedContactIds.size > 0);

  const handleClose = isSending
    ? () => setShowCloseWarning(true)
    : nav.exitAndDiscard;

  return (
    <IonHeader>
      <IonToolbar>
        <IonButtons slot="start">
          {onBack && (
            <IonButton
              onClick={onBack}
              aria-label="Back"
              data-testid="share-wizard-back"
            >
              <IonIcon
                slot="icon-only"
                icon={arrowBackOutline}
                aria-hidden="true"
              />
            </IonButton>
          )}
        </IonButtons>
        <IonTitle className={title ? undefined : 'ion-hide-accessible'}>
          {title ?? 'Share via SMS'}
        </IonTitle>
        <IonButtons slot="end">
          <IonButton
            onClick={handleClose}
            aria-label="Close"
            data-testid="share-wizard-close"
          >
            <IonIcon slot="icon-only" icon={closeOutline} aria-hidden="true" />
          </IonButton>
        </IonButtons>
      </IonToolbar>
      <IonProgressBar
        value={progress}
        color="success"
        data-testid="share-wizard-progress"
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '12px 20px',
          background: '#fff',
        }}
      >
        <span
          style={{
            color: '#999',
            fontSize: pxToRem(15),
            fontWeight: 'bold',
            flex: '0 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {topicTitle}
        </span>
        {progressLabel && (
          <span
            style={{
              color: '#999',
              fontSize: pxToRem(15),
              fontWeight: 'bold',
              flex: '0 0 auto',
              whiteSpace: 'nowrap',
            }}
          >
            {' '}
            - {progressLabel}
          </span>
        )}
      </div>
    </IonHeader>
  );
};

export default ShareWizardHeader;
